import * as XLSX from "xlsx";
import {
  invoiceControlRow,
  invoicePaidAt,
  settleStateLabel,
} from "./invoiceControlRow";
import {
  invoiceIncomeProductName,
  invoiceIsPack,
} from "./invoiceIncomeType";
import {
  BUSINESS_TZ,
  formatInUlaanbaatar,
} from "../utils/ulaanbaatarTime";

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const purposeText = (value) => {
  const text = String(value ?? "").trim();
  return text || null;
};

const uniquePurposeTexts = (values) => {
  const seen = [];
  (values || []).forEach((value) => {
    const text = purposeText(value);
    if (text && !seen.includes(text)) seen.push(text);
  });
  return seen;
};

function formatPurposeCell(record) {
  const groups = [
    {
      label: "Нэхэмжлэл",
      texts: uniquePurposeTexts([record.description]),
    },
    {
      label: "Мөр",
      texts: uniquePurposeTexts(
        (record.invoiceItems || []).map((item) => item.description)
      ),
    },
    {
      label: "Банк",
      texts: uniquePurposeTexts(record.bankDescriptions),
    },
  ];
  const lines = [];
  groups.forEach((group) => {
    const texts = group.texts.length ? group.texts : [null];
    texts.forEach((text, index) => {
      const prefix = index === 0 ? `${group.label}: ` : "  ";
      lines.push(text ? `${prefix}${text}` : `${prefix}—`);
    });
  });
  return lines.join("\n");
}

function formatRecipientNames(record) {
  const recipients = record.recipients || [];
  if (!recipients.length) return "";
  return recipients
    .map((recipient) => recipient.name || "")
    .join("\n");
}

function formatRecipientAccounts(record) {
  const recipients = record.recipients || [];
  if (!recipients.length) return "";
  return recipients
    .map((recipient) => recipient.account || "")
    .filter(Boolean)
    .join("\n");
}

function formatIncomeType(record) {
  const pack = invoiceIsPack(record);
  const productName = invoiceIncomeProductName(record);
  const base = pack ? "Багц" : "Дан";
  return productName ? `${base} — ${productName}` : base;
}

function formatInvoiceStatus(status) {
  return status === "PAID" ? "Төлөгдсөн" : "Төлөгдөөгүй";
}

function formatBankIncomeStatus(record) {
  return record.bankIncomeAmount != null ? "Орж ирсэн" : "Орж ирээгүй";
}

function formatSettleState(row) {
  if (row.settleState === "MIXED") {
    return row.settleParts
      .map((part) => `${part.id ?? "-"} ${settleStateLabel(part.state)}`)
      .join("\n");
  }
  return settleStateLabel(row.settleState);
}

function formatInstantCell(value) {
  return value ? formatInUlaanbaatar(value) : "";
}

const UB_INSTANT = "(УБ, UTC+8)";

const CONTROL_HEADERS = [
  "№",
  "Мерчант",
  "Нэхэмжлэлийн ID",
  `Нэхэмжлэл үүссэн огноо ${UB_INSTANT}`,
  "Төрөл",
  "Гүйлгээний утга",
  "Нэхэмжлэлийн дүн",
  "Шимтгэл",
  "Нийт дүн",
  "Төлбөрийн суваг",
  "Шилжүүлэх данс",
  "Хүлээн авагчийн нэр",
  "Хүлээн авагчийн данс",
  "Төлөв",
  `Төлөгдсөн цаг ${UB_INSTANT}`,
  `Сүүлд шинэчлэгдсэн цаг ${UB_INSTANT}`,
  "Орлогын дүн",
  "Орлогийн төлөв",
  "Өндөрлөгөө",
  "Өндөрлөгөөний төлөв",
  `Өндөрлөгсөн цаг ${UB_INSTANT}`,
  "Merchant %",
  "Мерчантын шимтгэл",
  "XPay %",
  "XPay дүн",
  "SPay дүн",
  "Боловсруулалт %",
  "Боловсруулалтын дүн",
  "Эцсийн дүн",
];

function percentCell(value) {
  return value == null ? null : Number(Number(value).toFixed(2));
}

/** One invoice row — columns match admin invoice control table. */
export function toInvoiceControlExcelRow(invoice, index) {
  const control = invoiceControlRow(invoice);
  const paidAt = invoicePaidAt(invoice);
  const createdKey = `Нэхэмжлэл үүссэн огноо ${UB_INSTANT}`;
  const paidKey = `Төлөгдсөн цаг ${UB_INSTANT}`;
  const updatedKey = `Сүүлд шинэчлэгдсэн цаг ${UB_INSTANT}`;
  const settledKey = `Өндөрлөгсөн цаг ${UB_INSTANT}`;

  return {
    "№": index,
    Мерчант: invoice.merchant?.name || "",
    "Нэхэмжлэлийн ID": invoice.id,
    [createdKey]: formatInstantCell(invoice.createdAt),
    Төрөл: formatIncomeType(invoice),
    "Гүйлгээний утга": formatPurposeCell(invoice),
    "Нэхэмжлэлийн дүн": num(invoice.amount),
    Шимтгэл: num(invoice.feeAmount),
    "Нийт дүн": num(invoice.totalAmount),
    "Төлбөрийн суваг": invoice.paymentMethod?.name || "",
    "Шилжүүлэх данс": control.fromAccountNumber || "",
    "Хүлээн авагчийн нэр": formatRecipientNames(invoice),
    "Хүлээн авагчийн данс": formatRecipientAccounts(invoice),
    Төлөв: formatInvoiceStatus(invoice.status),
    [paidKey]: formatInstantCell(paidAt),
    [updatedKey]: formatInstantCell(invoice.updatedAt),
    "Орлогын дүн": num(invoice.bankIncomeAmount),
    "Орлогийн төлөв": formatBankIncomeStatus(invoice),
    Өндөрлөгөө: control.settleIds || "",
    "Өндөрлөгөөний төлөв": formatSettleState(control),
    [settledKey]: control.settledAt
      ? formatInUlaanbaatar(control.settledAt, "YYYY-MM-DD HH:mm")
      : "",
    "Merchant %": percentCell(control.merchantFeePercent),
    "Мерчантын шимтгэл": num(control.merchantFee),
    "XPay %": percentCell(control.xpayFeePercent),
    "XPay дүн": num(control.xpayFee),
    "SPay дүн": num(control.spayFee),
    "Боловсруулалт %": percentCell(control.processingFeePercent),
    "Боловсруулалтын дүн": num(control.processingFee),
    "Эцсийн дүн": num(control.afterFeeAmount),
  };
}

function summaryExcelRow(summary) {
  const paidKey = `Төлөгдсөн цаг ${UB_INSTANT}`;
  const updatedKey = `Сүүлд шинэчлэгдсэн цаг ${UB_INSTANT}`;
  return {
    "№": "",
    Мерчант: "",
    "Нэхэмжлэлийн ID": "",
    [`Нэхэмжлэл үүссэн огноо ${UB_INSTANT}`]: "",
    Төрөл: "",
    "Гүйлгээний утга": "Нийт",
    "Нэхэмжлэлийн дүн": num(summary?.totalAmount),
    Шимтгэл: num(summary?.feeAmount),
    "Нийт дүн": num(summary?.invoiceTotalAmount),
    "Төлбөрийн суваг": "",
    "Шилжүүлэх данс": "",
    "Хүлээн авагчийн нэр": "",
    "Хүлээн авагчийн данс": "",
    Төлөв: "",
    [paidKey]: "",
    [updatedKey]: "",
    "Орлогын дүн": num(summary?.bankIncomeAmount),
    "Орлогийн төлөв": "",
    Өндөрлөгөө: "",
    "Өндөрлөгөөний төлөв": "",
    [`Өндөрлөгсөн цаг ${UB_INSTANT}`]: "",
    "Merchant %": "",
    "Мерчантын шимтгэл": num(summary?.merchantFee),
    "XPay %": "",
    "XPay дүн": num(summary?.xpayFee),
    "SPay дүн": num(summary?.spayFee),
    "Боловсруулалт %": "",
    "Боловсруулалтын дүн": num(summary?.processingFee),
    "Эцсийн дүн": num(summary?.afterFeeAmount),
  };
}

export function buildAdminInvoiceWorkbook(payload) {
  const { rows = [], summary = {}, startDate, endDate, exportedAt } = payload;
  const exportedUb =
    exportedAt != null && exportedAt !== ""
      ? formatInUlaanbaatar(exportedAt)
      : formatInUlaanbaatar(new Date());

  const excelRows = rows.map((row, i) => toInvoiceControlExcelRow(row, i + 1));
  excelRows.push(summaryExcelRow(summary));

  const meta = [
    { Талбар: "Цагийн бүс", Утга: `${BUSINESS_TZ} (UTC+8)` },
    {
      Талбар: "Шүүлт (invoice.createdAt, УБ өдөр)",
      Утга: startDate && endDate ? `${startDate} → ${endDate}` : "",
    },
    { Талбар: "Экспорт хийсэн", Утга: exportedUb },
    { Талбар: "Мөр", Утга: rows.length },
    {
      Талбар: "Төлөгдсөн цаг багана",
      Утга: "invoice.paidAt — зөвхөн PAID, proxy/backfill байхгүй",
    },
    {
      Талбар: "Сүүлд шинэчлэгдсэн",
      Утга: "invoice.updatedAt (callback гэх мэт)",
    },
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(meta),
    "Тойм"
  );
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(excelRows, { header: CONTROL_HEADERS }),
    "Орлогын гүйлгээнүүд"
  );

  return workbook;
}

export function downloadAdminInvoiceExcel(payload) {
  const workbook = buildAdminInvoiceWorkbook(payload);
  const compact = (d) => String(d || "export").replace(/-/g, "");
  const fileName = `InvoiceControl_${compact(payload.startDate)}_${compact(
    payload.endDate
  )}.xlsx`;
  XLSX.writeFile(workbook, fileName);
}
